// src/services/requestBatcher.js
// Request batching, deduplication, and parallel execution utility

/**
 * In-flight request tracker for deduplication
 * Key: request hash, Value: Promise
 */
const inFlightRequests = new Map();

/**
 * Pending batch requests waiting to be executed
 */
let batchQueue = [];
let batchTimeout = null;
const BATCH_DELAY = 10; // ms to wait before executing batch

/**
 * Generate a unique hash for a request based on its parameters
 */
function generateRequestHash(tableName, query, method = 'select') {
  const queryString = JSON.stringify(query || {});
  return `${method}:${tableName}:${queryString}`;
}

/**
 * Execute multiple async functions in parallel with error handling
 * @param {Array<Function>} fetchFunctions - Array of async functions to execute
 * @returns {Promise<Array>} Results array (maintains order)
 */
export async function parallel(fetchFunctions) {
  const results = await Promise.allSettled(fetchFunctions.map(fn => fn()));

  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return { success: true, data: result.value, index };
    }
    return { success: false, error: result.reason?.message || 'Unknown error', index };
  });
}

/**
 * Execute multiple async functions in parallel, returning when all succeed
 * Throws if any fail
 * @param {Array<Function>} fetchFunctions - Array of async functions
 * @returns {Promise<Array>} Array of results
 */
export async function parallelAll(fetchFunctions) {
  return Promise.all(fetchFunctions.map(fn => fn()));
}

/**
 * Execute async functions in parallel with a concurrency limit
 * @param {Array<Function>} fetchFunctions - Array of async functions
 * @param {number} limit - Maximum concurrent requests (default: 5)
 * @returns {Promise<Array>} Results array
 */
export async function parallelWithLimit(fetchFunctions, limit = 5) {
  const results = [];
  const executing = [];

  for (const [index, fn] of fetchFunctions.entries()) {
    const promise = Promise.resolve().then(() => fn()).then(
      (data) => ({ success: true, data, index }),
      (error) => ({ success: false, error: error.message, index })
    );

    results.push(promise);

    if (limit <= fetchFunctions.length) {
      const executingPromise = promise.then(() => {
        const idx = executing.indexOf(executingPromise);
        if (idx > -1) executing.splice(idx, 1);
      });
      executing.push(executingPromise);

      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
  }

  return Promise.all(results);
}

/**
 * Deduplicated fetch - prevents duplicate in-flight requests
 * If an identical request is already in progress, returns that promise
 * @param {string} key - Unique key for the request
 * @param {Function} fetchFn - Async function to execute
 * @returns {Promise} Result of fetch
 */
export async function deduplicatedFetch(key, fetchFn) {
  // Check if identical request is already in flight
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  // Create new request promise
  const requestPromise = fetchFn()
    .then((result) => {
      inFlightRequests.delete(key);
      return result;
    })
    .catch((error) => {
      inFlightRequests.delete(key);
      throw error;
    });

  // Store in flight request
  inFlightRequests.set(key, requestPromise);

  return requestPromise;
}

/**
 * Batch multiple requests and execute them together
 * Useful for reducing the number of round trips
 * @param {Object} request - Request configuration
 * @returns {Promise} Result of the batched request
 */
export function batchRequest(request) {
  return new Promise((resolve, reject) => {
    batchQueue.push({ request, resolve, reject });

    // Clear existing timeout
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    // Set new timeout to execute batch
    batchTimeout = setTimeout(executeBatch, BATCH_DELAY);
  });
}

/**
 * Execute all pending batched requests
 */
async function executeBatch() {
  const batch = batchQueue;
  batchQueue = [];
  batchTimeout = null;

  if (batch.length === 0) return;

  // Group requests by table for potential optimization
  const grouped = batch.reduce((acc, item) => {
    const table = item.request.table || 'default';
    if (!acc[table]) acc[table] = [];
    acc[table].push(item);
    return acc;
  }, {});

  // Execute each group
  for (const [table, items] of Object.entries(grouped)) {
    try {
      // Execute all requests in parallel
      const results = await Promise.allSettled(
        items.map(item => item.request.execute())
      );

      // Resolve/reject each request
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          items[index].resolve(result.value);
        } else {
          items[index].reject(result.reason);
        }
      });
    } catch (error) {
      // Reject all items in this group
      items.forEach(item => item.reject(error));
    }
  }
}

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @returns {Promise} Result of successful execution
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    shouldRetry = (error) => true,
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Calculate delay with exponential backoff + jitter
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
        maxDelay
      );

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Create an abort controller with timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Object} { controller, signal, cleanup }
 */
export function createAbortableRequest(timeoutMs = 30000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return {
    controller,
    signal: controller.signal,
    cleanup: () => clearTimeout(timeoutId),
    abort: () => {
      clearTimeout(timeoutId);
      controller.abort();
    },
  };
}

/**
 * Race multiple promises with a timeout
 * @param {Promise} promise - Promise to race
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} errorMessage - Error message on timeout
 * @returns {Promise} Result or timeout error
 */
export function withTimeout(promise, timeoutMs = 30000, errorMessage = 'Request timeout') {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
}

/**
 * Debounce a function
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function with cancel method
 */
export function debounce(fn, delay = 300) {
  let timeoutId = null;

  const debounced = function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    return new Promise((resolve) => {
      timeoutId = setTimeout(async () => {
        const result = await fn.apply(this, args);
        resolve(result);
      }, delay);
    });
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  debounced.flush = async (...args) => {
    debounced.cancel();
    return fn.apply(this, args);
  };

  return debounced;
}

/**
 * Throttle a function
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Minimum time between calls in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(fn, limit = 300) {
  let inThrottle = false;
  let lastArgs = null;

  const throttled = function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          throttled.apply(this, lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };

  return throttled;
}

/**
 * Create a request queue that processes requests sequentially
 * Useful for operations that must not run in parallel
 */
export function createRequestQueue() {
  const queue = [];
  let processing = false;

  async function processQueue() {
    if (processing || queue.length === 0) return;

    processing = true;

    while (queue.length > 0) {
      const { fn, resolve, reject } = queue.shift();
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    processing = false;
  }

  return {
    add(fn) {
      return new Promise((resolve, reject) => {
        queue.push({ fn, resolve, reject });
        processQueue();
      });
    },
    clear() {
      queue.length = 0;
    },
    get length() {
      return queue.length;
    },
  };
}

/**
 * Batch loader for efficiently loading multiple items by ID
 * Collects individual requests and batches them together
 */
export function createBatchLoader(batchFn, options = {}) {
  const {
    maxBatchSize = 100,
    batchDelay = 10,
    cacheResults = true,
  } = options;

  const cache = new Map();
  let batch = [];
  let batchPromise = null;
  let timeoutId = null;

  async function executeBatch() {
    const currentBatch = batch;
    batch = [];
    batchPromise = null;

    if (currentBatch.length === 0) return;

    // Get unique IDs
    const ids = [...new Set(currentBatch.map(item => item.id))];

    try {
      // Execute batch fetch
      const results = await batchFn(ids);

      // Create result map
      const resultMap = new Map();
      if (Array.isArray(results)) {
        results.forEach(item => {
          if (item && item.id) {
            resultMap.set(item.id, item);
            if (cacheResults) {
              cache.set(item.id, item);
            }
          }
        });
      }

      // Resolve all promises
      currentBatch.forEach(({ id, resolve, reject }) => {
        const result = resultMap.get(id);
        if (result) {
          resolve(result);
        } else {
          resolve(null);
        }
      });
    } catch (error) {
      // Reject all promises
      currentBatch.forEach(({ reject }) => reject(error));
    }
  }

  return {
    load(id) {
      // Check cache first
      if (cacheResults && cache.has(id)) {
        return Promise.resolve(cache.get(id));
      }

      return new Promise((resolve, reject) => {
        batch.push({ id, resolve, reject });

        // Execute immediately if batch is full
        if (batch.length >= maxBatchSize) {
          if (timeoutId) clearTimeout(timeoutId);
          executeBatch();
          return;
        }

        // Schedule batch execution
        if (!timeoutId) {
          timeoutId = setTimeout(() => {
            timeoutId = null;
            executeBatch();
          }, batchDelay);
        }
      });
    },
    loadMany(ids) {
      return Promise.all(ids.map(id => this.load(id)));
    },
    prime(id, value) {
      cache.set(id, value);
    },
    clear(id) {
      if (id) {
        cache.delete(id);
      } else {
        cache.clear();
      }
    },
  };
}

// Export all utilities
const requestBatcher = {
  parallel,
  parallelAll,
  parallelWithLimit,
  deduplicatedFetch,
  batchRequest,
  retryWithBackoff,
  createAbortableRequest,
  withTimeout,
  debounce,
  throttle,
  createRequestQueue,
  createBatchLoader,
};

export default requestBatcher;
